import { HoldingsSecurityDetails } from '@/interfaces/raDashboard.interface'
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useHoldingsSecurityDetails } from '../RADashboardDataContext/hooks'

export type HoldingsMetric = 'securityRisk' | 'securityRiskContrib'
export type ShowContributors = 'topBottom' | 'topOnly' | 'bottomOnly'

export interface HoldingsTopBottom {
  topItems: HoldingsSecurityDetails[],
  bottomItems: HoldingsSecurityDetails[]
}

interface HoldingsTabContextValue {
  metric: HoldingsMetric
  setMetric: (m: HoldingsMetric) => void
  showContributors: ShowContributors
  setShowContributors: (v: ShowContributors) => void
  contributorCount: 5 | 10 | 15
  setContributorCount: (n: 5 | 10 | 15) => void
  securitiesFiltered: boolean
  highlightGroups: string[] | null,
  setHighlightGroups: (...groups: string[]) => void,
  highlightedSecurities: HoldingsSecurityDetails[]
  highlightSecurities: (securityIds: string[]) => void
  clearHighlightedSecurities: () => void
}

const HoldingsTabContext = createContext<HoldingsTabContextValue>({
  metric: 'securityRisk',
  setMetric: () => {},
  showContributors: 'topBottom',
  setShowContributors: () => {},
  contributorCount: 5,
  setContributorCount: () => {},
  securitiesFiltered: false,
  highlightGroups: null,
  setHighlightGroups: () => {},
  highlightedSecurities: [],
  highlightSecurities: () => {},
  clearHighlightedSecurities: () => {}
})
const EMPTY_SECURITIES: HoldingsSecurityDetails[] = [];
export const HoldingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const fullDetails = useHoldingsSecurityDetails();
  const [highlightedSecurities, setHighlightedSecurities] = useState<string[] | null>(null);
  const [highlightGroups, setHighlightGroups] = useState<string[] | null>(null);
  const [metric, setMetric] = useState<HoldingsMetric>('securityRisk')
  const [showContributors, setShowContributors] = useState<ShowContributors>('topBottom')
  const [contributorCount, setContributorCount] = useState<5 | 10 | 15>(5)
  const securitiesFiltered = highlightedSecurities != null;

  const filteredSecurities = useMemo((): HoldingsSecurityDetails[] => {
    if (fullDetails == null) return EMPTY_SECURITIES;
    if (highlightedSecurities == null) return fullDetails;
    return fullDetails.filter((sec) => highlightedSecurities.includes(sec.identifier));
  }, [fullDetails, highlightedSecurities]);

  const clearHighlightedSecurities = useCallback(() => {
    setHighlightedSecurities(null);
    setHighlightGroups(null);
  }, []);

  return (
    <HoldingsTabContext.Provider value={{
      metric, setMetric, securitiesFiltered,
      showContributors, setShowContributors,
      contributorCount, setContributorCount,
      highlightGroups, setHighlightGroups: (...groups) => setHighlightGroups(groups),
      highlightedSecurities: filteredSecurities, highlightSecurities: setHighlightedSecurities,
      clearHighlightedSecurities
    }}>
      {children}
    </HoldingsTabContext.Provider>
  )
}

export const useHoldingsMetric = () => useContext(HoldingsTabContext)

export const useHighlightedSecurities = () => {
  const ctx = useHoldingsMetric();
  return ctx.highlightedSecurities;
}

export const useHighlightedGroups = () => {
  const ctx = useHoldingsMetric();
  return ctx.highlightGroups;
}

export const useTopBottomSecurities = () => {
  const ctx = useHoldingsMetric();
  const { metric, showContributors, contributorCount, highlightedSecurities } = ctx;
  const topBotItems: HoldingsTopBottom = useMemo(() => {
    const showBottom = showContributors === "bottomOnly";
    if (highlightedSecurities.length <= contributorCount) {
      return {
        topItems: showBottom ? [] : highlightedSecurities,
        bottomItems: showBottom ? highlightedSecurities : []
      };
    }
    const sorted = [...highlightedSecurities].sort((a, b) => (b[metric] ?? 0) - (a[metric] ?? 0))
    return {
      topItems: sorted.slice(0, contributorCount),
      bottomItems: sorted.slice(-contributorCount).reverse(),
    }
  }, [metric, contributorCount, highlightedSecurities, showContributors]);
  return topBotItems;
}